export const FLAVOR_PREFIX_TO_VALUE = [
  { prefix: "blueberry", value: "blueberry", emoji: "🫐" },
  { prefix: "green apple", value: "green-apple", emoji: "🍏" },
  { prefix: "tangerine", value: "tangerine", emoji: "🍊" },
  { prefix: "raspberry lemonade", value: "raspberry-lemonade", emoji: "🍇🍋" },
];

const MEDICATION_FORM_PREFERENCE_CODE = "insomnia-medication-form-preference";
const GUMMY_FLAVOR_PREFERENCE_CODE =
  "insomnia-medication-gummy-flavor-preference";
const AUTOSHIP_PREFERENCE_CODE = "insomnia-medication-autoship";
const MEDICATION_INTEREST_CODE = "medications-interested-in-checkbox";

// Match the explicit checkbox labels, including state notices and the older
// questionnaire spelling of Prazosin. Never infer interests from free text.
const MEDICATION_INTEREST_NAMES = {
  cyclobenzaprine: "cyclobenzaprine",
  doxepin: "doxepin",
  gabapentin: "gabapentin",
  guanfacine: "guanfacine",
  hydroxyzine: "hydroxyzine",
  prazosin: "prazosin",
  prozasin: "prazosin",
  quetiapine: "quetiapine",
  ramelteon: "ramelteon",
  trazodone: "trazodone",
};

function getMedicationInterests(intakeJSON) {
  const answer = findIntakeQuestion(intakeJSON, MEDICATION_INTEREST_CODE);
  return Object.values(answer || {}).flatMap((choice) => {
    if (choice?.checked !== true || typeof choice.title !== "string") return [];
    const name = choice.title
      .trim()
      .toLowerCase()
      .split(/[\s[(<]/)[0];
    const medication = MEDICATION_INTEREST_NAMES[name];
    return typeof medication === "string" ? [medication] : [];
  });
}

// Handles both the flat shape (visit.consultIntakes[].intakeJSON) and the
// nested shape used in localStorage, which wraps answers under a
// `{consultationType}-{version}` key: { "inf-2024090901": { 0: {...}, 1: {...} } }.
function findIntakeQuestion(intakeJSON, code) {
  if (!intakeJSON || typeof intakeJSON !== "object") return null;
  for (const value of Object.values(intakeJSON)) {
    if (value?.question?.code === code) {
      return value;
    }
    if (value && typeof value === "object") {
      for (const inner of Object.values(value)) {
        if (inner?.question?.code === code) {
          return inner;
        }
      }
    }
  }
  return null;
}

/**
 * Returns the patient's medication-form preference from an intake answers object.
 * @param {Object} intakeJSON - Either consultAnswers (from localStorage) or a consult_intake's intakeJSON.
 * @returns {"tablets"|"gummy"|"no-preference"|null}
 */
export function getMedicationFormPreference(intakeJSON) {
  const question = findIntakeQuestion(
    intakeJSON,
    MEDICATION_FORM_PREFERENCE_CODE,
  );
  if (!question) return null;
  if ([0, "0"].includes(question.selectedIndex)) return "tablets";
  if ([1, "1"].includes(question.selectedIndex)) return "gummy";
  if ([2, "2"].includes(question.selectedIndex)) return "no-preference";
  return null;
}

/**
 * Returns the flavor value (e.g. "blueberry") the patient selected on the intake's
 * gummy child question, or null if they didn't choose gummy or didn't pick a flavor.
 */
export function getGummyFlavorFromIntake(intakeJSON) {
  const question = findIntakeQuestion(
    intakeJSON,
    MEDICATION_FORM_PREFERENCE_CODE,
  );
  if (!question) return null;
  if (![1, "1"].includes(question.selectedIndex)) return null;
  const selectedIndex = 1;
  const childAnswers = question[selectedIndex]?.childAnswers;
  const flavorAnswer = childAnswers?.[GUMMY_FLAVOR_PREFERENCE_CODE];
  if (typeof flavorAnswer?.selectedValue !== "string") return null;
  const selectedFlavorLower = flavorAnswer.selectedValue.toLowerCase();
  for (const { prefix, value } of FLAVOR_PREFIX_TO_VALUE) {
    if (selectedFlavorLower.startsWith(prefix)) return value;
  }
  return null;
}

/**
 * Visit-level helper used by the EMR: prefers the order_item subcategory (what was
 * actually ordered), falling back to the intake answer for older visits.
 * @returns {string|null} flavor value (e.g. "green-apple") or null
 */
export function getFlavorPreferenceFromVisit(visit) {
  const subcategory = visit?.orderItems?.[0]?.subcategory;
  if (subcategory) return subcategory;
  if (!visit?.consultIntakes?.length) return null;
  for (const intake of visit.consultIntakes) {
    const flavor = getGummyFlavorFromIntake(intake?.intakeJSON);
    if (flavor) return flavor;
  }
  return null;
}

// Only current-visit preferences drive the prescribing picker. In particular,
// missing indices must not coerce to 0 (the autoship "Yes" choice).
export function getSleepPrescriptionPreferences(visit) {
  const intakes = visit?.consultIntakes || [];
  const flavor = getFlavorPreferenceFromVisit(visit);
  const hasOrderedFlavor = !!visit?.orderItems?.[0]?.subcategory;
  const intakeForm = intakes
    .map((intake) => getMedicationFormPreference(intake?.intakeJSON))
    .find((preference) => preference !== null);
  const formPreference = flavor ? "gummy" : intakeForm || null;
  return {
    flavor,
    formPreference,
    formPreferenceSource: hasOrderedFlavor
      ? "order"
      : formPreference && formPreference !== "no-preference"
        ? "intake"
        : null,
    interestedMedications: [
      ...new Set(
        intakes.flatMap((intake) => getMedicationInterests(intake?.intakeJSON)),
      ),
    ],
    requestsAutoship: intakes.some((intake) => {
      const answer = findIntakeQuestion(
        intake?.intakeJSON,
        AUTOSHIP_PREFERENCE_CODE,
      );
      return [0, "0"].includes(answer?.selectedIndex);
    }),
  };
}
